/**
 * Shared keyframes + style mixins for the configurator-v3 wizard.
 *
 * Replaces the standalone app's global `index.css` animation classes with
 * styled-components-scoped keyframes (hashed, no global class leakage). Import a
 * keyframe into a `.styles.ts` and reference it in an `animation:` shorthand.
 * Colour values come from `configurator-tokens` — none live here.
 */
import { css, keyframes } from 'styled-components';

import { CFG, CFG_ANIM, CFG_SCROLLBAR_THUMB } from './configurator-tokens';
// The owner-studio glow-border hover (soft cursor-following wash + animated 1px
// hairline). Reused here so the wizard's cards/tiles share the studio affordance.
import { hoverWash as studioGlowHover } from './mixins';

/** Pill entrance: fade + slight scale-in. */
export const pillIn = keyframes`
  from { opacity: 0; scale: 0.92; }
  to { opacity: 1; scale: 1; }
`;

/** Pill grow: expand max-width (mobile pack-scan). */
export const pillGrow = keyframes`
  from { max-width: 56px; }
  to { max-width: 600px; }
`;

/** Pill name slide-in from the left. */
export const pillName = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: none; }
`;

/** Scale-pop (decorations). */
export const pop = keyframes`
  from { opacity: 0; scale: 0.4; }
  to { opacity: 1; scale: 1; }
`;

/**
 * ConfigPrefs popover (UserMenu) entrance: fade in while scaling up and
 * settling down a few px — paired with `transform-origin: top right` it reads
 * as the card unfolding out of the account icon.
 */
export const popupIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

/**
 * Card/tile entrance — fade in with a slight rise + settle. Replicates the owner
 * studio's acquisition campaign-card entrance (its `Cell` `cardIn`) exactly, so a
 * freshly-swapped set of cards "comes in" the same way across the product. Used by
 * the art-style tiles when the colour changes and their set is replaced.
 */
export const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/**
 * Apply the `cardIn` entrance on mount (self-fading, holds its end state), with the
 * standard reduced-motion opt-out. Same duration + ease as the acquisition campaign
 * cards. Put it on a card/tile root; re-runs whenever that element remounts (e.g. a
 * list keyed on the selected colour so the whole new set animates in on a swap).
 */
export const cardSwapIn = css`
  animation: ${cardIn} ${CFG_ANIM.CARD_IN_MS}ms ${CFG_ANIM.EASE_CARD_IN} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** One full rotation. */
export const spin = keyframes`
  to { transform: rotate(1turn); }
`;

/** Border-beam fade in then out. */
export const beamFade = keyframes`
  0% { opacity: 0; }
  12% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
`;

/** Border-beam single revolution (centred). */
export const beamSpin = keyframes`
  to { transform: translate(-50%, -50%) rotate(1turn); }
`;

/** Thin, subtle scrollbar for side panes (was `.scroll-thin`). */
export const thinScrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: ${CFG_SCROLLBAR_THUMB} transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${CFG_SCROLLBAR_THUMB};
    border-radius: 9999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

/** Scrollable area with no visible scrollbar (was `.scroll-none`). */
export const hiddenScrollbar = css`
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Hover affordance for a clickable card: a faint white wash that fades in over
 * the whole surface on hover (and keyboard focus). A pointer-events-none
 * `::after` overlay sits above the card's artwork/content, inherits its
 * `border-radius`, and is independent of the card's own background. A faint,
 * dimmed white (`CFG.wash`) — a mild lift, not a strong highlight. Mirrors the
 * area-wide `hoverWash` in `theme/mixins`, scoped to the configurator's token
 * universe (same pattern as `thinScrollbar` above). Apply to a clickable card
 * root. (This faint local wash is kept only for the `cardHover` switch; the live
 * wizard hover is the studio glow-border `hoverWash` — see `cardHover` below.)
 */
export const hoverWash = css`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${CFG.wash};
    opacity: 0;
    pointer-events: none;
    transition: opacity ${CFG_ANIM.INTERACT_MS}ms ease;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
  }
`;

/**
 * Grow affordance for a clickable card: a subtle scale-up on hover / keyboard
 * focus. `transform` is GPU-composited, so it never reflows the neighbouring
 * cards; `z-index` lifts the hovered card cleanly above them. Same family of
 * effect as the Design step's colour swatches / art-style tiles (those keep a
 * slightly larger scale tuned to their small size). Apply to a card root.
 *
 * NOTE: this sets the `transition` shorthand to `transform`; a card that also
 * animates another property (e.g. a `border-color` hover) will have that
 * property snap rather than ease while grow is active — acceptable for the
 * scale-dominated effect, and moot once switched back to `hoverWash`.
 */
export const hoverGrow = css`
  position: relative;
  transition: transform ${CFG_ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    transform: scale(1.03);
    z-index: 1;
  }
`;

/**
 * The active hover affordance for the configurator's clickable CARDS + preview
 * tiles (design themes, colour swatches, art-style tiles, feature / pack cards,
 * player options). Points at the owner-studio glow-border hover (`theme/mixins`
 * hoverWash): a cursor-following soft wash + an animated 1px glow hairline border,
 * the same affordance interactive elements use across the studio. Flip this single
 * alias to switch the whole wizard's card-hover style in ONE place — no per-card
 * edits. (The animated highlight pills and the per-tier LevelTabs keep their own
 * local `hoverWash`; the local `hoverGrow`/`hoverWash` above stay for that switch.)
 *
 * Cursor tracking needs the `usePointerGlow` controller, mounted in the configurator
 * root; without it the glow gracefully degrades to a soft centred version.
 */
export const cardHover = studioGlowHover;
